import React, { useCallback, useEffect, useMemo } from "react";
import { Marker as MapMarker } from "react-map-gl";
import { MarkerIconStyles } from "../../Map/MapStyles";
import { markerSVGSwitch } from "../../../shared/components/MarkerImages";
import classNames from "classnames";
import { useEditState } from "../EditContext";
import { useMapData } from "./MapDataContext";
import { EditorMapPoi, SelectedLink } from "../../../utils/editorUtils";
import { MapPoi, Poi } from "../../../types";
import { useRestAuth } from "../../../hooks/useRestAuth";
import { MapPointLabel } from "./../EditorStyles";

interface EditorPoiMarkerProps {
  index: number;
  point: EditorMapPoi;
  selectedLink: SelectedLink | undefined;
  setSelectedLink: React.Dispatch<React.SetStateAction<SelectedLink | undefined>>;
  setLinkEditorLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setLinkEditorCoordinatesLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshingData: React.Dispatch<React.SetStateAction<boolean>>;
  unlinked?: boolean;
}

const EditorPoiMarker: React.FC<EditorPoiMarkerProps> = ({
  index,
  point,
  selectedLink,
  setSelectedLink,
  setLinkEditorLoading,
  setLinkEditorCoordinatesLoading,
  setRefreshingData,
  unlinked,
}) => {
  const { poiRepository, mapPoiRepository, target, editData, mapPanoRepository } = useEditState();
  const { updateLayerData, markAllLayersStale } = useMapData();
  const restAuth = useRestAuth();
  const [poi, setPoi] = React.useState<Poi | null>(null);
  const disabled =
    unlinked || (selectedLink && (selectedLink.type !== "poi" || selectedLink.item !== point.poi));

  // Helper function to refresh layer data after toggle (only for target layers)
  const refreshLayerData = useCallback(async () => {
    if (!restAuth || !target) return;
    
    setRefreshingData(true);
    
    // For specific layer: fetch all layer data again
    const panoLinks = await mapPanoRepository.get(restAuth, target);
    const poiLinks = await mapPoiRepository.get(restAuth, target);
    const unlinkedPanoLinks = await mapPanoRepository.get_unlinked(restAuth, target);
    const unlinkedPoiLinks = await mapPoiRepository.get_unlinked(restAuth, target);
    const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
    const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);
    
    const refreshedData = {
      panorama_location: panoLinks,
      poi_location: poiLinks,
      unlinked_panorama_location: unlinkedPanoLinks,
      unlinked_poi_location: unlinkedPoiLinks,
      unused_panorama_location: unusedPanoLinks,
      unused_poi_location: unusedPoiLinks,
    };
    
    // Update cache with fresh data
    updateLayerData(target, refreshedData);
    editData<"map">(refreshedData);
    
    setRefreshingData(false);
  }, [restAuth, target, mapPanoRepository, mapPoiRepository, updateLayerData, editData, setRefreshingData]);

  useEffect(() => {
    const fetchPoi = async () => {
      if (restAuth) {
        const fetchedPoi = await poiRepository.single(restAuth, point.poi);
        setPoi(fetchedPoi);
      }
    };
    fetchPoi();
  }, [poiRepository, point, restAuth]);

  const offset = useMemo(() => {
    if (poi) {
      switch (poi.type) {
        case "pano":
        case "video":
        case "vcr":
          return { offsetLeft: -18, offsetTop: -18 };
        case "faq":
          return { offsetLeft: -22, offsetTop: -37 };
        default:
          return { offsetLeft: -15, offsetTop: -37 };
      }
    }
  }, [poi]);

  const handleDragEnd = useCallback(
    async (event) => {
      if (restAuth) {
        if (selectedLink?.item !== index) {
          setLinkEditorLoading(true);
          setSelectedLink({ type: "poi", item: point.poi });
        } else {
          setLinkEditorCoordinatesLoading(true);
        }
        const lat = event.lngLat.lat;
        const long = event.lngLat.lng;
        const links = await mapPoiRepository.update(restAuth, { lat, long }, point.poi);
        
        // For global map: update only the poi_location data
        editData<"map">({ poi_location: links });
        
        // If this is a global edit (no target), mark all layers as stale
        if (!target) {
          markAllLayersStale();
        }
        
        if (selectedLink?.item !== index) {
          setLinkEditorLoading(false);
        } else {
          setLinkEditorCoordinatesLoading(false);
        }
      }
    },
    [
      editData,
      index,
      mapPoiRepository,
      point.poi,
      restAuth,
      selectedLink?.item,
      setLinkEditorCoordinatesLoading,
      setLinkEditorLoading,
      setSelectedLink,
      target,
      markAllLayersStale,
    ]
  );

  if (!offset || !poi) return null;
  return (
    <MapMarker
      latitude={point.lat}
      longitude={point.long}
      offset={[offset.offsetLeft, offset.offsetTop]}
      anchor="top-left"
      draggable={!target}
      onDragEnd={!target ? handleDragEnd : undefined}
      onClick={async (event) => {
        if (!target) {
          setSelectedLink({ type: "poi", item: point.poi });
        } else {
          if (restAuth) {
            if (unlinked) {
              await mapPoiRepository.create(restAuth, { poi: point.poi }, target);
            } else {
              await mapPoiRepository.delete(restAuth, point.poi, target);
            }
            
            // Refresh layer data na toggle (alleen voor layer-specifieke acties)
            await refreshLayerData();
          }
        }
      }}
    >
      <div style={{ display: "flex" }}>
        <MarkerIconStyles
          className={classNames({
            question: poi.type === "faq",
          })}
        >
          {markerSVGSwitch(
            { type: poi.type } as MapPoi,
            false,
            disabled ? "--color-neutral-50" : point.color,
            disabled ? 0.4 : 1
          )}
        </MarkerIconStyles>
        <MapPointLabel $disabled={disabled}>{point.title}</MapPointLabel>
      </div>
    </MapMarker>
  );
};

export const MemoizedEditorPoiMarker = React.memo(EditorPoiMarker);
